const WRAPPER_CLASS = "reach-product-description-readmore"
const BODY_CLASS = "reach-product-description-readmore__body"
const TOGGLE_CLASS = "reach-product-description-readmore__toggle"
const MIN_PREVIEW_PX = 96

function findDescriptionTarget(): HTMLElement | null {
  const module = document.querySelector(
    ".ecwid-productBrowser .product-details-module.product-details__product-description",
  )
  if (module instanceof HTMLElement) {
    const content =
      module.querySelector(":scope > .product-details-module__content") ||
      module.querySelector(".product-details-module__content")
    if (content instanceof HTMLElement) {
      return content
    }
    return module
  }

  const fallback = document.querySelector(
    ".ecwid-productBrowser .product-details__product-description",
  )
  return fallback instanceof HTMLElement ? fallback : null
}

function findGallery(): HTMLElement | null {
  const selectors = [
    ".ecwid-productBrowser .product-details__pictures",
    ".ecwid-productBrowser .product-details-pictures",
    ".ecwid-productBrowser .details-gallery",
    ".ecwid-productBrowser .product-details__image",
  ]
  for (const selector of selectors) {
    const element = document.querySelector(selector)
    if (element instanceof HTMLElement) {
      return element
    }
  }
  return null
}

function flattenNestedAccordions(root: HTMLElement) {
  root.querySelectorAll("details").forEach((nested) => {
    nested.open = true
    nested.querySelector(":scope > summary")?.remove()
  })
}

function unwrapLegacyAccordion() {
  document.querySelectorAll(".reach-product-description-accordion").forEach((details) => {
    const parent = details.parentElement
    if (!parent) {
      return
    }

    while (details.firstChild) {
      const child = details.firstChild
      if (child instanceof HTMLElement && child.tagName === "SUMMARY") {
        child.remove()
        continue
      }
      parent.insertBefore(child, details)
    }
    details.remove()
  })
}

function absorbLooseContent(container: HTMLElement, wrap: HTMLElement) {
  const body = wrap.querySelector(`.${BODY_CLASS}`)
  if (!(body instanceof HTMLElement)) {
    return
  }

  Array.from(container.childNodes).forEach((node) => {
    if (node !== wrap) {
      body.appendChild(node)
    }
  })
}

function ensureReadMore(container: HTMLElement): HTMLElement {
  const existing = container.querySelector(`:scope > .${WRAPPER_CLASS}`)
  if (existing instanceof HTMLElement) {
    absorbLooseContent(container, existing)
    return existing
  }

  const wrap = document.createElement("div")
  wrap.className = WRAPPER_CLASS

  const body = document.createElement("div")
  body.className = BODY_CLASS
  while (container.firstChild) {
    body.appendChild(container.firstChild)
  }

  const toggle = document.createElement("button")
  toggle.type = "button"
  toggle.className = TOGGLE_CLASS
  toggle.textContent = "Läs mer..."

  wrap.appendChild(body)
  wrap.appendChild(toggle)
  container.appendChild(wrap)

  toggle.addEventListener("click", () => {
    const expanded = wrap.classList.toggle("is-expanded")
    toggle.textContent = expanded ? "Visa mindre" : "Läs mer..."
    updateClamp(wrap)
  })

  return wrap
}

function updateClamp(wrap: HTMLElement) {
  const body = wrap.querySelector(`.${BODY_CLASS}`)
  if (!(body instanceof HTMLElement)) {
    return
  }

  const toggle = wrap.querySelector(`.${TOGGLE_CLASS}`)
  if (wrap.classList.contains("is-expanded")) {
    wrap.classList.remove("is-clamped")
    body.style.maxHeight = ""
    if (toggle instanceof HTMLElement) {
      toggle.hidden = false
    }
    return
  }

  const gallery = findGallery()
  body.style.maxHeight = ""
  wrap.classList.remove("is-clamped")

  let available = MIN_PREVIEW_PX
  if (gallery) {
    available = Math.floor(
      gallery.getBoundingClientRect().bottom - body.getBoundingClientRect().top - 28,
    )
    if (available < MIN_PREVIEW_PX) {
      available = MIN_PREVIEW_PX
    }
  }

  if (body.scrollHeight > available + 8) {
    wrap.classList.add("is-clamped")
    body.style.maxHeight = `${available}px`
    if (toggle instanceof HTMLElement) {
      toggle.hidden = false
    }
    return
  }

  if (toggle instanceof HTMLElement) {
    toggle.hidden = true
  }
}

function applyProductDescriptionReadMore() {
  unwrapLegacyAccordion()

  const description = findDescriptionTarget()
  if (!description) {
    return
  }

  const module =
    description.closest(".product-details-module") instanceof HTMLElement
      ? (description.closest(".product-details-module") as HTMLElement)
      : description

  module.querySelectorAll(".product-details-module__title").forEach((title) => title.remove())
  module.querySelectorAll(".product-details-module__content--collapsed").forEach((content) => {
    content.classList.remove("product-details-module__content--collapsed")
  })
  flattenNestedAccordions(description)

  const wrap = ensureReadMore(description)
  flattenNestedAccordions(wrap)
  updateClamp(wrap)
}

function onProductPageLoaded() {
  requestAnimationFrame(() => {
    applyProductDescriptionReadMore()
    window.setTimeout(applyProductDescriptionReadMore, 80)
    window.setTimeout(applyProductDescriptionReadMore, 300)
    window.setTimeout(applyProductDescriptionReadMore, 800)
  })
}

function whenEcwidPageApiReady(callback: () => void) {
  if (window.Ecwid?.OnPageLoaded) {
    callback()
    return
  }

  const startedAt = Date.now()
  const intervalId = window.setInterval(() => {
    if (window.Ecwid?.OnPageLoaded) {
      window.clearInterval(intervalId)
      callback()
      return
    }

    if (Date.now() - startedAt > 15_000) {
      window.clearInterval(intervalId)
    }
  }, 50)
}

export function initProductDescriptionAccordion() {
  whenEcwidPageApiReady(() => {
    const handlePage = (page: { type: string }) => {
      if (page.type !== "PRODUCT") {
        return
      }
      onProductPageLoaded()
    }

    window.Ecwid?.OnPageLoaded.add(handlePage)
    window.Ecwid?.OnPageSwitch.add(handlePage)
    window.addEventListener("resize", () => {
      const wrap = document.querySelector(`.${WRAPPER_CLASS}`)
      if (wrap instanceof HTMLElement) {
        updateClamp(wrap)
      }
    })
    onProductPageLoaded()
  })
}
